import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Tooltip, Popup, GeoJSON, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import api, { num, eur } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, MapPin, Users, Euro, Layers, X, ChevronRight, Target } from "lucide-react";
import VestaBadge from "@/components/VestaBadge";

const CLASS_STYLE = { A: "bg-[#FDD003] text-black border-[#FDD003]", B: "bg-[#F59E0B] text-white border-[#F59E0B]", C: "bg-slate-400 text-white border-slate-400", D: "bg-slate-600 text-white border-slate-600" };
const CLASS_COLOR = { A: "#FDD003", B: "#F59E0B", C: "#94A3B8", D: "#64748B" };
const CLASS_DESC = [["A", "Strategici"], ["B", "Consolidati"], ["C", "Occasionali"], ["D", "Da attivare"]];

function MapController({ focus, points }) {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 200);
    return () => clearTimeout(t);
  }, [map]);
  useEffect(() => {
    map.invalidateSize();
    if (focus) {
      if (points && points.length > 0) {
        map.fitBounds(points, { padding: [45, 45], maxZoom: 12, animate: true });
      } else if (focus.lat != null) {
        map.flyTo([focus.lat, focus.lng], 9, { duration: 0.8 });
      }
    } else {
      map.flyTo([42.2, 12.5], 5, { duration: 0.8 });
    }
  }, [focus, points, map]);
  return null;
}

export default function ItalyMap({ focusRequest }) {
  const navigate = useNavigate();
  const [agencies, setAgencies] = useState(null);
  const [selected, setSelected] = useState(null);
  const [classSel, setClassSel] = useState(null);
  const [showRegions, setShowRegions] = useState(false);
  const [geo, setGeo] = useState(null);
  const [showClients, setShowClients] = useState(false);
  const [clients, setClients] = useState(null);
  const [focused, setFocused] = useState(null);

  useEffect(() => {
    api.get("/dashboard/agencies-map").then((r) => {
      setAgencies(r.data);
      if (r.data.length) setSelected(r.data[0]);
    }).catch(() => setAgencies([]));
  }, []);

  useEffect(() => {
    if (focusRequest && agencies && agencies.length) {
      const a = agencies.find((x) => x.name === focusRequest.name);
      if (a) { setSelected(a); setFocused(a); }
    }
  }, [focusRequest, agencies]);

  useEffect(() => {
    if (showRegions && !geo) {
      fetch("https://raw.githubusercontent.com/openpolis/geojson-italy/master/geojson/limits_IT_regions.geojson")
        .then((r) => r.json()).then(setGeo).catch(() => {});
    }
  }, [showRegions, geo]);

  useEffect(() => {
    if ((showClients || focused) && clients === null) {
      api.get("/dashboard/clients-map").then((r) => setClients(r.data)).catch(() => setClients([]));
    }
  }, [showClients, focused, clients]);

  const focusedPartners = useMemo(
    () => (focused ? (clients || []).filter((c) => c.agency_name === focused.name) : []),
    [focused, clients],
  );

  const focusPoints = useMemo(() => {
    const pts = [];
    if (focused?.lat != null) pts.push([focused.lat, focused.lng]);
    focusedPartners.forEach((c) => { if (c.lat != null && c.lng != null) pts.push([c.lat, c.lng]); });
    return pts;
  }, [focused, focusedPartners]);

  const max = useMemo(() => Math.max(1, ...(agencies || []).map((a) => a.revenue || 0)), [agencies]);
  const color = (rev) => { const r = rev / max; return r > 0.66 ? "#1d4ed8" : r > 0.33 ? "#3b82f6" : "#93c5fd"; };
  const radius = (rev) => 9 + Math.round((rev / max) * 20);

  const coverage = useMemo(() => {
    const m = {};
    (agencies || []).forEach((a) => {
      (a.regions_served || []).forEach((r) => { const c = m[r] = m[r] || { agencies: new Set(), partners: 0, revenue: 0 }; c.agencies.add(a.name); });
      (a.by_region || []).forEach((b) => { const c = m[b.region] = m[b.region] || { agencies: new Set(), partners: 0, revenue: 0 }; c.partners += b.partners; c.revenue += b.revenue; });
    });
    return m;
  }, [agencies]);
  const maxRegRev = useMemo(() => Math.max(1, ...Object.values(coverage).map((c) => c.revenue || 0)), [coverage]);
  const normReg = (n) => (n || "").split("/")[0].trim();
  const regionStyle = (feature) => {
    const c = coverage[normReg(feature.properties.reg_name)];
    if (c && c.agencies.size > 0) {
      const ratio = (c.revenue || 0) / maxRegRev;
      return { color: "#1d4ed8", weight: 1, fillColor: "#2563eb", fillOpacity: 0.15 + ratio * 0.4 };
    }
    return { color: "#cbd5e1", weight: 1, fillColor: "#94a3b8", fillOpacity: 0.08 };
  };
  const onEachRegion = (feature, layer) => {
    const name = normReg(feature.properties.reg_name);
    const c = coverage[name];
    const ag = c && c.agencies.size ? Array.from(c.agencies).join(", ") : "nessuna";
    layer.bindTooltip(`<b>${name}</b><br/>Agenzie: ${ag}<br/>${c ? c.partners : 0} clienti`, { sticky: true });
  };

  if (agencies === null)
    return <div className="flex h-[420px] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="grid gap-4 lg:grid-cols-3" data-testid="italy-map">
      <div className="relative z-0 h-[460px] min-w-0 overflow-hidden rounded-md border border-border lg:col-span-2" data-testid="leaflet-map">
        {!focused ? (
          <>
            <Button variant={showRegions ? "default" : "outline"} size="sm" onClick={() => setShowRegions((v) => !v)} data-testid="toggle-regions" className="absolute right-2 top-2 z-[1000] shadow">
              <Layers className="mr-1.5 h-3.5 w-3.5" /> Aree servite
            </Button>
            <Button variant={showClients ? "default" : "outline"} size="sm" onClick={() => setShowClients((v) => !v)} data-testid="toggle-clients" className="absolute right-2 top-12 z-[1000] shadow">
              <Users className="mr-1.5 h-3.5 w-3.5" /> Clienti
            </Button>
            {showClients && (
              <div className="absolute bottom-2 left-2 z-[1000] flex items-center gap-3 rounded-md border border-border bg-background/90 px-2 py-1 text-[11px] shadow" data-testid="clients-legend">
                <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-[#16a34a]" /> Partner</span>
                <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-[#f59e0b]" /> Cliente</span>
              </div>
            )}
          </>
        ) : (
          <>
            <Button variant="default" size="sm" onClick={() => setFocused(null)} data-testid="map-clear-focus" className="absolute left-1/2 top-2 z-[1000] -translate-x-1/2 shadow-lg">
              <X className="mr-1.5 h-3.5 w-3.5" /> Tutte le agenzie
            </Button>
            <div className="absolute bottom-2 right-2 z-[1000] max-w-[55%] truncate rounded-md border border-border bg-background/90 px-2 py-1 text-[11px] font-semibold shadow" data-testid="map-focus-name">
              {focused.name} · {num(focused.partners_count)} clienti
            </div>
            <div className="absolute bottom-2 left-2 z-[1000] flex items-center gap-3 rounded-md border border-border bg-background/90 px-2 py-1 text-[11px] shadow">
              <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-[#16a34a]" /> Partner</span>
              <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-[#f59e0b]" /> Cliente</span>
            </div>
          </>
        )}
        <MapContainer center={[42.2, 12.5]} zoom={5} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
          <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <MapController focus={focused} points={focusPoints} />
          {focused ? (
            <>
              {focused.lat != null && (
                <CircleMarker center={[focused.lat, focused.lng]} radius={12}
                  pathOptions={{ color: "#fff", weight: 3, fillColor: "#1d4ed8", fillOpacity: 0.9 }}>
                  <Tooltip direction="top" offset={[0, -4]} permanent><div className="text-xs"><b>{focused.name}</b></div></Tooltip>
                </CircleMarker>
              )}
              {focusedPartners.map((cl) => (
                <CircleMarker key={cl.id} center={[cl.lat, cl.lng]} radius={6}
                  pathOptions={{ color: "#fff", weight: 1, fillColor: cl.is_partner ? "#16a34a" : "#f59e0b", fillOpacity: 0.9 }}>
                  <Tooltip direction="top" offset={[0, -2]}>
                    <div className="text-xs"><b>{cl.name}</b> · {cl.is_partner ? "Partner" : "Cliente"}<br />{cl.city || cl.region || "—"} · {eur(cl.importo_totale)}{cl.is_partner && cl.vesta_punti != null ? ` · Vesta ${cl.vesta_punti}` : ""}</div>
                  </Tooltip>
                  <Popup>
                    <div className="text-xs">
                      <div className="font-bold">{cl.name}</div>
                      <div className="text-muted-foreground">{cl.is_partner ? "Partner" : "Cliente"} · {cl.city || cl.region || "—"}</div>
                      <div className="mt-1">Fatturato: <b>{eur(cl.importo_totale)}</b></div>
                      {cl.is_partner && (
                        <div className="mt-0.5 flex items-center gap-1 font-bold text-amber-600">
                          <img src="/vesta-badge.png" alt="Vesta" className="inline-block h-4 w-4 rounded-full object-contain" />
                          <span className="text-[#E3B341]">punti Vesta: {cl.vesta_punti != null ? cl.vesta_punti.toLocaleString("it-IT", { maximumFractionDigits: 1 }) : "—"}</span>
                        </div>
                      )}
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </>
          ) : (
            <>
              {showRegions && geo && <GeoJSON key="regions-overlay" data={geo} style={regionStyle} onEachFeature={onEachRegion} />}
              {showClients && (clients || []).map((cl) => (
                <CircleMarker key={cl.id} center={[cl.lat, cl.lng]} radius={5}
                  pathOptions={{ color: "#fff", weight: 1, fillColor: cl.is_partner ? "#16a34a" : "#f59e0b", fillOpacity: 0.9 }}>
                  <Tooltip direction="top" offset={[0, -2]}>
                    <div className="text-xs"><b>{cl.name}</b> · {cl.is_partner ? "Partner" : "Cliente"}<br />{cl.city || cl.region || "—"}{cl.agency_name ? ` · ${cl.agency_name}` : ""} · {eur(cl.importo_totale)}{cl.is_partner && cl.vesta_punti != null ? ` · Vesta ${cl.vesta_punti}` : ""}</div>
                  </Tooltip>
                </CircleMarker>
              ))}
              {agencies.map((a) => (
                <CircleMarker
                  key={a.id}
                  center={[a.lat, a.lng]}
                  radius={radius(a.revenue)}
                  pathOptions={{ color: "#fff", weight: selected?.id === a.id ? 3 : 1.5, fillColor: color(a.revenue), fillOpacity: 0.85 }}
                  eventHandlers={{ click: () => { setSelected(a); setFocused(a); } }}
                >
                  <Tooltip direction="top" offset={[0, -4]}>
                    <div className="text-xs"><b>{a.name}</b><br />{a.sede_city || "—"} · {num(a.partners_count)} clienti · {eur(a.revenue)}</div>
                  </Tooltip>
                </CircleMarker>
              ))}
            </>
          )}
        </MapContainer>
      </div>

      <div className="min-w-0 lg:col-span-1" data-testid="agency-esploso">
        {!selected ? (
          <div className="flex h-full items-center justify-center rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            Clicca su un pin per vedere il dettaglio dell'agenzia.
          </div>
        ) : (
          <div className="flex h-[460px] flex-col rounded-md border border-border">
            <div className="flex shrink-0 items-start justify-between gap-2 border-b border-border p-4">
              <div>
                <div className="font-heading text-base font-bold tracking-tight">{selected.name}</div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3 w-3" /> {selected.sede_city || "Sede n/d"}{selected.referente ? ` · ${selected.referente}` : ""}</div>
              </div>
              <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: color(selected.revenue) }} />
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto">
            <div className="space-y-2 border-b border-border p-4">
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-md bg-muted/50 p-2"><div className="flex items-center gap-1 text-[10px] uppercase text-muted-foreground"><Euro className="h-3 w-3" /> Vendite</div><div className="font-heading text-lg font-black">{eur(selected.revenue)}</div></div>
                <div className="rounded-md bg-muted/50 p-2"><div className="flex items-center gap-1 text-[10px] uppercase text-muted-foreground"><Users className="h-3 w-3" /> Clienti</div><div className="font-heading text-lg font-black">{num(selected.partners_count)}</div></div>
                <div className="col-span-2 rounded-md border-2 border-primary/50 bg-primary/10 p-3 shadow-[0_0_0_1px_rgba(253,208,3,0.15)]" data-testid="agency-sow-top20">
                  <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-primary"><Target className="h-3.5 w-3.5" /> Peso · Top 20% clienti</div>
                  <div className="font-heading text-3xl font-black leading-tight text-primary">{selected.sow_top20 != null ? `${selected.sow_top20}%` : "N/D"}</div>
                  <div className="mt-1 text-[10px] leading-tight text-muted-foreground">
                    Quota del <b className="text-foreground">fatturato TORRI dell'agente</b> generata dal <b className="text-foreground">20% dei clienti più grandi</b>{selected.sow_top20_count ? ` (${selected.sow_top20_count} client${selected.sow_top20_count === 1 ? "e" : "i"})` : ""}. Più è alta, più il fatturato è concentrato su pochi clienti chiave.
                  </div>
                </div>
              </div>
              <div className="rounded-md bg-muted/50 p-3" data-testid="classi-partner-histogram">
                <div className="mb-2 text-[10px] uppercase tracking-wide text-muted-foreground">Classi clienti · clicca per l'elenco</div>
                <div className="space-y-1.5">
                  {(() => {
                    const maxv = Math.max(1, ...["A", "B", "C", "D"].map((x) => selected.class_counts[x] || 0));
                    return CLASS_DESC.map(([c, desc]) => {
                      const v = selected.class_counts[c] || 0;
                      return (
                        <button
                          type="button"
                          key={c}
                          onClick={() => setClassSel({ c, desc, list: (selected.partners || []).filter((p) => p.partner_class === c) })}
                          className="flex w-full items-center gap-2 rounded px-1 py-0.5 text-left transition-colors hover:bg-muted"
                          data-testid={`class-bar-${c}`}
                        >
                          <div className="flex w-24 shrink-0 items-baseline gap-1 text-[11px]"><b className="font-heading">{c}</b><span className="truncate text-muted-foreground">{desc}</span></div>
                          <div className="h-3.5 flex-1 overflow-hidden rounded-full bg-muted">
                            <div className="h-full rounded-full transition-all" style={{ width: `${Math.max(v > 0 ? 8 : 0, (v / maxv) * 100)}%`, backgroundColor: CLASS_COLOR[c] }} />
                          </div>
                          <div className="w-5 text-right font-mono text-xs font-bold">{v}</div>
                        </button>
                      );
                    });
                  })()}
                </div>
              </div>
              <div className="rounded-lg border-2 border-amber-400/60 bg-amber-400/10 p-3" data-testid="agency-vesta-slot">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <VestaBadge className="h-9 w-9" />
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-[#E3B341]">Saldo Vesta agenzia</div>
                      <div className="text-[10px] text-muted-foreground">Guadagnati {(selected.vesta_guadagnati ?? 0).toLocaleString("it-IT", { maximumFractionDigits: 1 })} · Spesi {(selected.vesta_spesi ?? 0).toLocaleString("it-IT", { maximumFractionDigits: 1 })}</div>
                    </div>
                  </div>
                  <div className="font-heading text-3xl font-black leading-none text-amber-500" data-testid="agency-vesta-saldo">{selected.vesta_saldo != null ? selected.vesta_saldo.toLocaleString("it-IT", { maximumFractionDigits: 1 }) : "—"}</div>
                </div>
              </div>
              <div className="rounded-lg border-2 border-emerald-500/40 p-3" data-testid="agency-ordini-group">
                <div className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-emerald-500"><Euro className="h-3 w-3" /> Dati ordini</div>
                <div className="grid grid-cols-2 gap-2" data-testid="agency-prodotti">
                  <div className="rounded-md bg-muted/50 p-2"><div className="text-[10px] uppercase text-muted-foreground">Tot pannelli FV</div><div className="font-heading text-lg font-black" data-testid="agency-tot-pannelli">{eur(selected.tot_pannelli_eur || 0)}</div><div className="text-[10px] text-muted-foreground">{selected.tot_pannelli_pct != null ? `${selected.tot_pannelli_pct}% del venduto` : "—"}</div></div>
                  <div className="rounded-md bg-muted/50 p-2"><div className="text-[10px] uppercase text-muted-foreground">Tot inverter</div><div className="font-heading text-lg font-black" data-testid="agency-tot-inverter">{eur(selected.tot_inverter_eur || 0)}</div><div className="text-[10px] text-muted-foreground">{selected.tot_inverter_pct != null ? `${selected.tot_inverter_pct}% del venduto` : "—"}</div></div>
                  <div className="rounded-md bg-muted/50 p-2"><div className="text-[10px] uppercase text-muted-foreground">Tot batterie</div><div className="font-heading text-lg font-black" data-testid="agency-tot-batterie">{eur(selected.tot_batterie_eur || 0)}</div><div className="text-[10px] text-muted-foreground">{selected.tot_batterie_pct != null ? `${selected.tot_batterie_pct}% del venduto` : "—"}</div></div>
                  <div className="rounded-md bg-muted/50 p-2"><div className="text-[10px] uppercase text-muted-foreground">Tot sistemi di fissaggio</div><div className="font-heading text-lg font-black" data-testid="agency-tot-fissaggio">{eur(selected.tot_fissaggio_eur || 0)}</div><div className="text-[10px] text-muted-foreground">{selected.tot_fissaggio_pct != null ? `${selected.tot_fissaggio_pct}% del venduto` : "—"}</div></div>
                </div>
                <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {selected.can_margin && (
                    <div className="rounded-md border-2 border-amber-400/60 bg-amber-400/10 p-3" data-testid="agency-sconto-medio">
                      <div className="text-[10px] font-bold uppercase tracking-wide text-amber-500">Sconto medio su commessa</div>
                      <div className="font-heading text-3xl font-black leading-tight text-amber-500">{selected.sconto_medio != null ? `${selected.sconto_medio}%` : "N/D"}</div>
                    </div>
                  )}
                  {(() => {
                    const v = selected.margine_contrib_medio;
                    const c = v == null ? { b: "border-primary/50", bg: "bg-primary/10", t: "text-primary" }
                      : v < 3 ? { b: "border-red-500/60", bg: "bg-red-500/10", t: "text-red-500" }
                      : v < 6 ? { b: "border-orange-500/60", bg: "bg-orange-500/10", t: "text-orange-500" }
                      : { b: "border-emerald-500/60", bg: "bg-emerald-500/10", t: "text-emerald-500" };
                    return (
                      <div className={`rounded-md border-2 ${c.b} ${c.bg} p-3`} data-testid="agency-margine-contrib">
                        <div className={`text-[10px] font-bold uppercase tracking-wide ${c.t}`}>Indice di contribuzione medio</div>
                        <div className={`font-heading text-3xl font-black leading-tight ${c.t}`}>{v != null ? `${v}` : "N/D"}</div>
                      </div>
                    );
                  })()}
                </div>
              </div>
              <div className="rounded-lg border-2 border-sky-500/40 p-3" data-testid="agency-preventivi-group">
                <div className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-sky-500"><Layers className="h-3 w-3" /> Dati preventivi</div>
                <div className="grid grid-cols-3 gap-2" data-testid="agency-preventivi">
                  <div className="rounded-md bg-muted/50 p-2"><div className="text-[10px] uppercase text-muted-foreground">Preventivi</div><div className="font-heading text-lg font-black" data-testid="agency-num-preventivi">{num(selected.num_preventivi || 0)}</div></div>
                  <div className="rounded-md bg-muted/50 p-2"><div className="text-[10px] uppercase text-muted-foreground">Valore preventivi</div><div className="font-heading text-lg font-black" data-testid="agency-valore-preventivi">{eur(selected.valore_preventivi || 0)}</div></div>
                  <div className="rounded-md border-2 border-primary/40 bg-primary/5 p-2"><div className="text-[10px] uppercase text-muted-foreground">Tasso di chiusura <span className="normal-case">(preventivi → ordini)</span></div><div className="font-heading text-lg font-black text-primary" data-testid="agency-tasso-conversione">{selected.tasso_conversione != null ? `${selected.tasso_conversione}%` : "N/D"}</div></div>
                </div>
              </div>
            </div>

            <div className="p-4">
              <div className="mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"><Layers className="h-3 w-3" /> Aree seguite</div>
              <div className="mb-3 flex flex-wrap gap-1">
                {(selected.regions_served || []).length ? selected.regions_served.map((r) => <Badge key={r} variant="outline" className="text-[11px]">{r}</Badge>) : <span className="text-xs text-muted-foreground">—</span>}
              </div>
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Clienti ({selected.partners.length})</div>
              <div className="space-y-1">
                {selected.partners.slice(0, 30).map((p, i) => (
                  <div key={i} className="rounded border border-border px-2 py-1.5 text-xs" data-testid={`esploso-partner-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate">{p.name}<span className="text-muted-foreground"> · {p.city || p.region || "—"}</span></span>
                      <div className="flex shrink-0 items-center gap-1">
                        {p.partner_class && <Badge className={`border px-1 text-[9px] ${CLASS_STYLE[p.partner_class] || ""}`}>{p.partner_class}</Badge>}
                        <span className="font-mono text-[11px]">{eur(p.importo_totale)}</span>
                      </div>
                    </div>
                    <div className="mt-1 flex items-center justify-between gap-2 text-[10px] text-muted-foreground">
                      {p.presidio_status && p.presidio_status !== "attivo"
                        ? <Badge variant="outline" className={`px-1 text-[9px] ${p.presidio_status === "cimitero" ? "border-red-500/40 text-red-400" : "border-amber-500/40 text-amber-400"}`} data-testid={`esploso-presidio-${i}`}>{p.presidio_status === "cimitero" ? "Cimitero" : "Panchina"}</Badge>
                        : <Badge variant="outline" className="border-emerald-500/30 px-1 text-[9px] text-emerald-400" data-testid={`esploso-presidio-${i}`}>Attivo</Badge>}
                      <span className="truncate" data-testid={`esploso-lastaction-${i}`}>
                        {p.last_action ? `${p.last_action.label} · ${String(p.last_action.date).split("-").reverse().join("/")}` : "Nessuna azione"}
                      </span>
                    </div>
                  </div>
                ))}
                {selected.partners.length === 0 && <span className="text-xs text-muted-foreground">Nessun cliente collegato.</span>}
              </div>
            </div>
            </div>
          </div>
        )}
      </div>

      <Dialog open={!!classSel} onOpenChange={(o) => !o && setClassSel(null)}>
        <DialogContent className="max-h-[80vh] overflow-hidden" data-testid="class-list-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {classSel && <span className="inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-black text-white" style={{ background: CLASS_COLOR[classSel.c] }}>{classSel.c}</span>}
              {classSel?.desc} <span className="text-sm font-normal text-muted-foreground">· {classSel?.list?.length || 0} clienti</span>
            </DialogTitle>
            <DialogDescription>{selected?.name}</DialogDescription>
          </DialogHeader>
          <div className="mt-1 max-h-[58vh] space-y-1 overflow-y-auto pr-1">
            {(classSel?.list || []).map((p) => (
              <button
                type="button"
                key={p.id}
                onClick={() => { navigate(`/partners/${p.id}`); setClassSel(null); }}
                className="flex w-full items-center justify-between gap-2 rounded border border-border px-2 py-2 text-left text-sm transition-colors hover:bg-muted"
                data-testid={`class-list-item-${p.id}`}
              >
                <span className="min-w-0 truncate">{p.name}<span className="text-muted-foreground"> · {p.city || p.region || "—"}</span></span>
                <span className="flex shrink-0 items-center gap-2">
                  <span className="font-mono text-xs font-semibold">{eur(p.importo_totale)}</span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </span>
              </button>
            ))}
            {classSel && classSel.list.length === 0 && <span className="text-sm text-muted-foreground">Nessun cliente in questa classe.</span>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
